import type {
	CoursesGradeQuizAnswerDto,
	CoursesListPendingQuizAttemptsQueryDto,
	CoursesReviewJournalEntryDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	ListPendingQuizAttemptsInput,
	ListPendingQuizAttemptsOutput,
	ListGradingLessonsOutput,
	GetQuizAttemptForGradingInput,
	GetQuizAttemptForGradingOutput,
	GradeQuizAnswerInput,
	GradeQuizAnswerOutput,
	ListGradeEventsForAttemptInput,
	ListGradeEventsForAttemptOutput,
	ListStudentQuizAttemptsInput,
	ListStudentQuizAttemptsOutput,
	ListJournalEntriesInput,
	ListJournalEntriesOutput,
	ReviewJournalEntryInput,
	ReviewJournalEntryOutput,
} from '../../types/admin-grading.types';

const TAG = 'courses:admin-grading' as const;
const INVALIDATES = [TAG, 'courses:challenges', 'courses:lessons'] as const;

export const adminGradingEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		listPendingQuizAttempts: builder.query<
			ListPendingQuizAttemptsOutput,
			ListPendingQuizAttemptsInput
		>({
			query: (params) => ({
				url: 'courses/admin/grading/pending',
				params: params satisfies Wire<CoursesListPendingQuizAttemptsQueryDto>,
			}),
			providesTags: [TAG],
		}),
		listGradingLessons: builder.query<ListGradingLessonsOutput, void>({
			query: () => ({ url: 'courses/admin/grading/lessons' }),
			providesTags: [TAG],
		}),
		getQuizAttemptForGrading: builder.query<
			GetQuizAttemptForGradingOutput,
			GetQuizAttemptForGradingInput
		>({
			query: ({ attemptId }) => ({
				url: `courses/admin/grading/attempts/${encodeURIComponent(attemptId)}`,
			}),
			providesTags: [TAG],
		}),
		listJournalEntries: builder.query<ListJournalEntriesOutput, ListJournalEntriesInput>({
			query: (params) => ({ url: 'courses/admin/grading/journals', params }),
			providesTags: [TAG],
		}),
		reviewJournalEntry: builder.mutation<ReviewJournalEntryOutput, ReviewJournalEntryInput>({
			query: ({ entryId, ...body }) => ({
				url: `courses/admin/grading/journals/${encodeURIComponent(entryId)}`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesReviewJournalEntryDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		gradeQuizAnswer: builder.mutation<GradeQuizAnswerOutput, GradeQuizAnswerInput>({
			query: ({ answerId, ...body }) => ({
				url: `courses/admin/grading/answers/${encodeURIComponent(answerId)}`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesGradeQuizAnswerDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		listGradeEventsForAttempt: builder.query<
			ListGradeEventsForAttemptOutput,
			ListGradeEventsForAttemptInput
		>({
			query: ({ attemptId }) => ({
				url: `courses/admin/grading/attempts/${encodeURIComponent(attemptId)}/events`,
			}),
			providesTags: [TAG],
		}),
		listStudentQuizAttempts: builder.query<
			ListStudentQuizAttemptsOutput,
			ListStudentQuizAttemptsInput
		>({
			query: ({ userId }) => ({
				url: `courses/admin/grading/students/${encodeURIComponent(userId)}/attempts`,
			}),
			providesTags: [TAG],
		}),
	}),
});

export const {
	useListPendingQuizAttemptsQuery,
	useLazyListPendingQuizAttemptsQuery,
	useListGradingLessonsQuery,
	useLazyListGradingLessonsQuery,
	useGetQuizAttemptForGradingQuery,
	useLazyGetQuizAttemptForGradingQuery,
	useGradeQuizAnswerMutation,
	useListGradeEventsForAttemptQuery,
	useLazyListGradeEventsForAttemptQuery,
	useListStudentQuizAttemptsQuery,
	useLazyListStudentQuizAttemptsQuery,
	useListJournalEntriesQuery,
	useReviewJournalEntryMutation,
} = adminGradingEndpoints;
